import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import EmojiPicker from "emoji-picker-react";
import api from "../../../shared/api/apiClient";
import styles from "./ChatPage.module.css";
import useChatSocket from "../hooks/useChatSocket";
import { useChat } from "../context/ChatContext";

const PAGE_SIZE = 50;
const RECENT_LIMIT = 15;

const notificationSound =
  typeof Audio !== "undefined" ? new Audio("/notification.mp3") : null;

function safeDecodeToken() {
  try {
    const t = localStorage.getItem("token");
    if (!t) return null;
    return jwtDecode(t);
  } catch {
    return null;
  }
}

function formatTime(dt) {
  if (!dt) return "";
  try {
    const d = new Date(dt);
    return d.toLocaleString();
  } catch {
    return "";
  }
}

function initials(name) {
  const s = (name || "").trim();
  if (!s) return "C";
  const parts = s.split(" ").filter(Boolean);
  const a = (parts[0] || "").slice(0, 1).toUpperCase();
  const b = (parts[1] || "").slice(0, 1).toUpperCase();
  return (a + b) || a || "C";
}

function isNearBottom(containerEl, thresholdPx = 140) {
  if (!containerEl) return true;
  const { scrollTop, scrollHeight, clientHeight } = containerEl;
  return scrollHeight - (scrollTop + clientHeight) < thresholdPx;
}

export default function ChatPage() {
  const { t } = useTranslation();
  const {
    clearUnread,
    unreadMap,
    presenceMap = {},
    setActiveChatThread,
    setChatPageOpened,
  } = useChat();

  const globalPresence = presenceMap || {};

  const getThreadTitle = useCallback(
    (thread) => {
      if (!thread) return "";
      if (thread.type === "group") return thread.title || t("chat_group_chat");
      return thread.title || t("chat_direct_chat");
    },
    [t]
  );

  const getThreadSubtitle = useCallback(
    (thread) => {
      if (!thread) return "";
      const parts = Array.isArray(thread.participants) ? thread.participants : [];
      if (!parts.length) return `${t("chat_participants_label")}: ${t("n_a")}`;
      const names = parts.map((p) => p.full_name).filter(Boolean);
      return names.length
        ? `${t("chat_participants_label")}: ${names.join(", ")}`
        : `${t("chat_participants_label")}: ${t("n_a")}`;
    },
    [t]
  );

  const renderSearchLabel = useCallback(
    (user) => {
      const ui = user?.ui_id ?? t("n_a");
      const name =
        user?.full_name ||
        `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
        t("chat_unknown_user");

      const email = user?.email ? ` (${user.email})` : "";
      return `${ui} — ${name}${email}`;
    },
    [t]
  );

  const me = useMemo(() => {
    const decoded = safeDecodeToken();
    const myId = decoded?.id ? Number(decoded.id) : null;
    return {
      id: myId,
      full_name: decoded?.full_name || t("chat_me"),
      email: decoded?.email || "",
    };
  }, [t]);

  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsError, setThreadsError] = useState("");

  const [activeThreadId, setActiveThreadId] = useState(null);
  const activeThread = useMemo(
    () => threads.find((thread) => Number(thread.id) === Number(activeThreadId)) || null,
    [threads, activeThreadId]
  );

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState("");
  const [nextCursor, setNextCursor] = useState(null);

  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const [search, setSearch] = useState("");

  const [presence, setPresence] = useState({});
  const [typingUsers, setTypingUsers] = useState({});
  const [readMap, setReadMap] = useState({});

  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [pickerMode, setPickerMode] = useState(null);
  const [groupStep, setGroupStep] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupTitle, setGroupTitle] = useState("");

  const bottomRef = useRef(null);
  const listRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);

  const activeThreadIdRef = useRef(activeThreadId);
  const myIdRef = useRef(me.id);
  const didAutoOpenRef = useRef(false);

  useEffect(() => {
    activeThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  useEffect(() => {
    myIdRef.current = me.id;
  }, [me.id]);

  useEffect(() => {
    setChatPageOpened?.(true);

    return () => {
      setChatPageOpened?.(false);
      setActiveChatThread?.(null);
    };
  }, [setActiveChatThread, setChatPageOpened]);

  useEffect(() => {
    setActiveChatThread?.(activeThreadId ? Number(activeThreadId) : null);
  }, [activeThreadId, setActiveChatThread]);

  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const TYPING_THROTTLE_MS = 700;
  const TYPING_STOP_MS = 1500;

  const scrollToBottom = (smooth = true) => {
    const el = bottomRef.current;
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "end" });
    } catch {}
  };

  const resetPicker = useCallback(() => {
    setShowCreateMenu(false);
    setPickerMode(null);
    setGroupStep(1);
    setUserSearch("");
    setUserResults([]);
    setRecentUsers([]);
    setUsersLoading(false);
    setSelectedUsers([]);
    setGroupTitle("");
  }, []);

  const loadThreads = useCallback(
    async (autoOpenFirstOnce = false) => {
      setThreadsLoading(true);
      setThreadsError("");
      try {
        const { data } = await api.get("/chat/threads");
        const items = Array.isArray(data) ? data : [];
        setThreads(items);

        if (autoOpenFirstOnce && !didAutoOpenRef.current) {
          if (!activeThreadId && items.length > 0) {
            didAutoOpenRef.current = true;
            setActiveThreadId(items[0].id);
          } else if (activeThreadId) {
            didAutoOpenRef.current = true;
          }
        }
      } catch (e) {
        setThreadsError(e?.response?.data?.detail || t("chat_failed_load_chats"));
      } finally {
        setThreadsLoading(false);
      }
    },
    [activeThreadId, t]
  );

  const loadMessages = useCallback(
    async (threadId, cursor = null, mode = "replace") => {
      if (!threadId) return;

      if (mode === "replace") {
        setMessages([]);
        setNextCursor(null);
        setMessagesError("");
        setMessagesLoading(true);
      } else {
        setMessagesLoading(true);
        setMessagesError("");
      }

      try {
        const params = { limit: PAGE_SIZE };
        if (cursor) params.cursor = cursor;

        const { data } = await api.get(`/chat/threads/${threadId}/messages`, {
          params,
        });
        const items = data?.items || [];
        const nc = data?.next_cursor ?? null;

        if (mode === "prepend") {
          const beforeHeight = listRef.current ? listRef.current.scrollHeight : null;

          setMessages((prev) => [...items, ...prev]);
          setNextCursor(nc);

          requestAnimationFrame(() => {
            if (!listRef.current || beforeHeight === null) return;
            const afterHeight = listRef.current.scrollHeight;
            const diff = afterHeight - beforeHeight;
            listRef.current.scrollTop = (listRef.current.scrollTop || 0) + diff;
          });
        } else {
          setMessages(items);
          setNextCursor(nc);
          requestAnimationFrame(() => scrollToBottom(false));
        }

        const last = items.length ? items[items.length - 1] : null;
        const lastId = last?.id;

        if (lastId) {
          api
            .post(`/chat/threads/${threadId}/read`, {
              last_read_message_id: lastId,
            })
            .catch(() => {});

          setThreads((prev) =>
            prev.map((thread) =>
              Number(thread.id) === Number(threadId) ? { ...thread, unread_count: 0 } : thread
            )
          );

          clearUnread?.(Number(threadId));
        }
      } catch (e) {
        setMessagesError(e?.response?.data?.detail || t("chat_failed_load_messages"));
      } finally {
        setMessagesLoading(false);
      }
    },
    [clearUnread, t]
  );

  const openThread = useCallback(
    (threadId) => {
      const tid = Number(threadId);
      setActiveThreadId(tid);
      setTypingUsers({});
      setMessagesError("");
      setShowEmojiPicker(false);
      clearUnread?.(tid);

      setThreads((prev) =>
        prev.map((thread) =>
          Number(thread.id) === tid ? { ...thread, unread_count: 0 } : thread
        )
      );
    },
    [clearUnread]
  );

  const loadOlder = async () => {
    if (!activeThreadId || !nextCursor) return;
    await loadMessages(activeThreadId, nextCursor, "prepend");
  };

  const loadUserResults = useCallback(async (q = "") => {
    const textValue = (q || "").trim();

    if (!textValue) {
      setUserResults([]);
      setUsersLoading(false);
      return;
    }

    setUsersLoading(true);
    try {
      const { data } = await api.get("/chat/users/search", {
        params: { q: textValue, limit: 30 },
      });
      setUserResults(Array.isArray(data) ? data : []);
    } catch {
      setUserResults([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const loadRecentUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const { data } = await api.get("/chat/users/recent", {
        params: { limit: RECENT_LIMIT },
      });
      setRecentUsers(Array.isArray(data) ? data : []);
    } catch {
      setRecentUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const createDirectByUiId = async (userUiId) => {
    try {
      const { data } = await api.post("/chat/threads/direct", {
        user_ui_id: Number(userUiId),
      });

      const newId = data?.id;
      resetPicker();
      await loadThreads(false);
      if (newId) openThread(newId);
    } catch (e) {
      alert(e?.response?.data?.detail || t("chat_failed_create_direct"));
    }
  };

  const createGroupByUiIds = async () => {
    const title = (groupTitle || "").trim();
    if (!title || !selectedUsers.length) return;

    try {
      const { data } = await api.post("/chat/threads/group", {
        title,
        user_ui_ids: selectedUsers.map((u) => Number(u.ui_id)),
      });

      const newId = data?.id;
      resetPicker();
      await loadThreads(false);
      if (newId) openThread(newId);
    } catch (e) {
      alert(e?.response?.data?.detail || t("chat_failed_create_group"));
    }
  };

  const toggleSelectUser = (user) => {
    setSelectedUsers((prev) => {
      const exists = prev.some((x) => Number(x.id) === Number(user.id));
      if (exists) {
        return prev.filter((x) => Number(x.id) !== Number(user.id));
      }
      return [...prev, user];
    });
  };

  const onEvent = useCallback(
    (evt) => {
      if (!evt?.type) return;

      if (evt.type === "pong") return;

      if (evt.type === "presence") {
        const uid = Number(evt.user_id);
        if (!uid) return;
        setPresence((p) => ({ ...p, [uid]: evt.status }));
        return;
      }

      if (evt.type === "typing") {
        const uid = Number(evt.user_id);
        if (!uid) return;

        const isTyping = !!evt.is_typing;
        setTypingUsers((p) => ({ ...p, [uid]: isTyping }));

        if (isTyping) {
          setTimeout(() => {
            setTypingUsers((pp) => ({ ...pp, [uid]: false }));
          }, 3000);
        }
        return;
      }

      if (evt.type === "read_state") {
        const threadId = Number(evt.thread_id);
        const reads = evt.reads || {};
        if (!threadId) return;

        const normalized = {};
        for (const [k, v] of Object.entries(reads)) {
          const uid = Number(k);
          if (!uid) continue;
          normalized[uid] = Number(v || 0);
        }

        setReadMap((prev) => {
          const current = prev[threadId] || {};
          return {
            ...prev,
            [threadId]: { ...current, ...normalized },
          };
        });

        return;
      }

      if (evt.type === "read") {
        const threadId = Number(evt.thread_id);
        const uid = Number(evt.user_id);
        const lastRead = Number(evt.last_read_message_id || 0);
        if (!threadId || !uid) return;

        setReadMap((prev) => {
          const tmap = prev[threadId] || {};
          const current = Number(tmap[uid] || 0);
          if (lastRead <= current) return prev;

          return {
            ...prev,
            [threadId]: {
              ...tmap,
              [uid]: lastRead,
            },
          };
        });

        return;
      }

      if (evt.type === "message") {
        const msg = evt.message;
        if (!msg?.id || !msg?.thread_id) return;

        const msgThreadId = Number(msg.thread_id);
        const currentThreadId = activeThreadIdRef.current
          ? Number(activeThreadIdRef.current)
          : null;
        const myId = myIdRef.current ? Number(myIdRef.current) : null;
        const fromMe = myId && Number(msg.sender_id) === myId;

        setThreads((prev) => {
          const idx = prev.findIndex((thread) => Number(thread.id) === msgThreadId);
          if (idx === -1) return prev;

          const thread = prev[idx];
          const isActive = currentThreadId && currentThreadId === msgThreadId;

          if (!fromMe && !isActive && notificationSound) {
            try {
              notificationSound.currentTime = 0;
              notificationSound.play().catch(() => {});
            } catch {}
          }

          const updated = {
            ...thread,
            last_message: msg.body,
            last_message_at: msg.created_at,
            unread_count: isActive || fromMe ? 0 : Number(thread.unread_count || 0) + 1,
          };

          const copy = [...prev];
          copy.splice(idx, 1);
          return [updated, ...copy];
        });

        if (fromMe) return;

        if (currentThreadId && currentThreadId === msgThreadId) {
          const shouldStick = isNearBottom(listRef.current);

          setMessages((prev) => {
            if (prev.some((m) => Number(m.id) === Number(msg.id))) return prev;
            return [...prev, msg];
          });

          api
            .post(`/chat/threads/${msgThreadId}/read`, {
              last_read_message_id: msg.id,
            })
            .catch(() => {});

          clearUnread?.(msgThreadId);

          setThreads((prev) =>
            prev.map((thread) =>
              Number(thread.id) === msgThreadId ? { ...thread, unread_count: 0 } : thread
            )
          );

          if (shouldStick) requestAnimationFrame(() => scrollToBottom(true));
        }
      }
    },
    [clearUnread]
  );

  const { send: wsSend } = useChatSocket({
    threadId: activeThreadId,
    onEvent,
  });

  const typingList = useMemo(() => {
    const parts = Array.isArray(activeThread?.participants)
      ? activeThread.participants
      : [];
    if (!parts.length) return [];
    return parts
      .filter((p) => p?.id && Number(p.id) !== Number(me.id) && typingUsers[p.id])
      .map((p) => p.full_name || `${t("log_user")} ${p.id}`);
  }, [activeThread, typingUsers, me.id, t]);

  const filteredThreads = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return threads;

    return threads.filter((thread) => {
      const title = (getThreadTitle(thread) || "").toLowerCase();
      const last = (thread.last_message || "").toLowerCase();
      const parts = Array.isArray(thread.participants) ? thread.participants : [];
      const names = parts.map((p) => (p.full_name || "").toLowerCase()).join(" ");
      return title.includes(q) || last.includes(q) || names.includes(q);
    });
  }, [threads, search, getThreadTitle]);

  const directPartner = useMemo(() => {
    if (!activeThread || activeThread.type !== "direct") return null;
    const parts = Array.isArray(activeThread.participants) ? activeThread.participants : [];
    return parts.find((p) => Number(p.id) !== Number(me.id)) || null;
  }, [activeThread, me.id]);

  const filteredSearchResults = userResults;
  const filteredRecentUsers = recentUsers;

  const sendMessage = async () => {
    const body = (text || "").trim();
    if (!body || !activeThreadId || sending) return;

    setSending(true);
    setMessagesError("");

    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    wsSend({ type: "typing", is_typing: false });

    const tempId = `tmp_${Date.now()}`;
    const optimistic = {
      id: tempId,
      thread_id: activeThreadId,
      body,
      created_at: new Date().toISOString(),
      sender_id: me.id,
      sender: { id: me.id, ui_id: null, full_name: me.full_name, email: me.email },
      is_deleted: false,
      edited_at: null,
    };

    setMessages((prev) => [...prev, optimistic]);
    setText("");
    setShowEmojiPicker(false);
    requestAnimationFrame(() => scrollToBottom(true));

    try {
      const { data } = await api.post(`/chat/threads/${activeThreadId}/messages`, { body });

      setMessages((prev) => prev.map((m) => (m.id === tempId ? data : m)));

      setThreads((prev) =>
        prev.map((thread) =>
          Number(thread.id) === Number(activeThreadId)
            ? { ...thread, last_message: data?.body, last_message_at: data?.created_at, unread_count: 0 }
            : thread
        )
      );

      clearUnread?.(Number(activeThreadId));

      if (data?.id) {
        api
          .post(`/chat/threads/${activeThreadId}/read`, {
            last_read_message_id: data.id,
          })
          .catch(() => {});
      }
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setMessagesError(e?.response?.data?.detail || t("chat_failed_send_message"));
      setText(body);
    } finally {
      setSending(false);
    }
  };

  const onEnterSend = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const onEmojiClick = useCallback((emojiData) => {
    setText((prev) => `${prev}${emojiData.emoji}`);
  }, []);

  useEffect(() => {
    loadThreads(true);
  }, [loadThreads]);

  useEffect(() => {
    if (activeThreadId) {
      loadMessages(activeThreadId, null, "replace");
    }
  }, [activeThreadId, loadMessages]);

  useEffect(() => {
    if (!pickerMode || (pickerMode === "group" && groupStep !== 1)) return;

    const textValue = (userSearch || "").trim();

    if (!textValue) {
      setUserResults([]);
      loadRecentUsers();
      return;
    }

    const timer = setTimeout(() => {
      loadUserResults(textValue);
    }, 250);

    return () => clearTimeout(timer);
  }, [pickerMode, groupStep, userSearch, loadUserResults, loadRecentUsers]);

  useEffect(() => {
    if (!showEmojiPicker) return;

    const handleOutsideClick = (event) => {
      const pickerEl = emojiPickerRef.current;
      const btnEl = emojiButtonRef.current;

      if (pickerEl?.contains(event.target)) return;
      if (btnEl?.contains(event.target)) return;

      setShowEmojiPicker(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showEmojiPicker]);

  const participants = Array.isArray(activeThread?.participants)
    ? activeThread.participants
    : [];
  const trimmedUserSearch = (userSearch || "").trim();
  const shouldShowUserResults = !!trimmedUserSearch && filteredSearchResults.length > 0;
  const shouldShowRecentUsers = !trimmedUserSearch && filteredRecentUsers.length > 0;

  const selectedCount = selectedUsers.length;

  const pickerTitle =
    pickerMode === "direct"
      ? t("chat_new_message")
      : pickerMode === "group" && groupStep === 2
      ? `${t("chat_new_group")} (${selectedCount})`
      : pickerMode === "group"
      ? `${t("chat_add_members")} (${selectedCount})`
      : t("chat_chats");

  const pickerSubtitle =
    pickerMode === "direct"
      ? t("chat_choose_employee")
      : pickerMode === "group" && groupStep === 2
      ? t("chat_set_group_details")
      : pickerMode === "group"
      ? selectedCount
        ? `${selectedCount} ${t("chat_selected")}`
        : t("chat_choose_employees")
      : t("chat_employees_only");

  const uniqueSelectedUsers = useMemo(() => {
    const seen = new Set();
    const out = [];

    for (const u of selectedUsers) {
      const key = Number(u.id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(u);
    }

    return out;
  }, [selectedUsers]);

  return (
    <div className={styles.chatPage}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarTop}>
          <div className={styles.brandBlock}>
            <div className={styles.sidebarTitle}>{pickerTitle}</div>
            <div className={styles.sidebarSubtitle}>{pickerSubtitle}</div>
          </div>

          <div className={styles.sidebarTopActions}>
            {pickerMode ? (
              <button
                className={styles.iconBtn}
                onClick={() => {
                  if (pickerMode === "group" && groupStep === 2) {
                    setGroupStep(1);
                    return;
                  }
                  resetPicker();
                }}
                title={t("chat_back")}
                type="button"
              >
                ←
              </button>
            ) : (
              <button
                className={styles.iconBtn}
                onClick={() => setShowCreateMenu((v) => !v)}
                title={t("chat_new_chat")}
                type="button"
              >
                +
              </button>
            )}
          </div>

          {!pickerMode && showCreateMenu && (
            <div className={styles.createMenu}>
              <button
                type="button"
                className={styles.createMenuItem}
                onClick={() => {
                  setShowCreateMenu(false);
                  setPickerMode("direct");
                  setGroupStep(1);
                  setSelectedUsers([]);
                  setGroupTitle("");
                  setUserSearch("");
                  setUserResults([]);
                  setRecentUsers([]);
                }}
              >
                {t("chat_new_message")}
              </button>

              <button
                type="button"
                className={styles.createMenuItem}
                onClick={() => {
                  setShowCreateMenu(false);
                  setPickerMode("group");
                  setGroupStep(1);
                  setSelectedUsers([]);
                  setGroupTitle("");
                  setUserSearch("");
                  setUserResults([]);
                  setRecentUsers([]);
                }}
              >
                {t("chat_new_group")}
              </button>
            </div>
          )}
        </div>

        {!pickerMode ? (
          <>
            <div className={styles.searchBox}>
              <input
                className={styles.searchInput}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("chat_search_chats")}
              />
            </div>

            {threadsError && <div className={styles.alertError}>{threadsError}</div>}
            {threadsLoading && <div className={styles.hint}>{t("chat_loading_chats")}</div>}

            <div className={styles.threadList}>
              {filteredThreads.map((thread) => {
                const isActive = Number(thread.id) === Number(activeThreadId);
                const threadIdNum = Number(thread.id);

                const unread =
                  unreadMap && Object.prototype.hasOwnProperty.call(unreadMap, threadIdNum)
                    ? Number(unreadMap[threadIdNum] || 0)
                    : Number(thread.unread_count || 0);

                const title = getThreadTitle(thread);
                const avatarText = initials(title);

                return (
                  <button
                    key={thread.id}
                    className={`${styles.threadItem} ${isActive ? styles.threadItemActive : ""}`}
                    onClick={() => openThread(thread.id)}
                    type="button"
                  >
                    <div className={styles.threadAvatar}>{avatarText}</div>

                    <div className={styles.threadBody}>
                      <div className={styles.threadTopRow}>
                        <div className={styles.threadName}>{title}</div>
                        <div className={styles.threadTime}>{formatTime(thread.last_message_at)}</div>
                      </div>

                      <div className={styles.threadBottomRow}>
                        <div className={styles.threadPreview}>{thread.last_message || t("chat_no_messages_yet")}</div>
                        {unread > 0 && <div className={styles.unreadBadge}>{unread}</div>}
                      </div>
                    </div>
                  </button>
                );
              })}

              {!threadsLoading && filteredThreads.length === 0 && (
                <div className={styles.hint}>{t("chat_no_chats_found")}</div>
              )}
            </div>
          </>
        ) : pickerMode === "group" && groupStep === 2 ? (
          <div className={styles.pickerScreen}>
            <div className={styles.groupStepTwoWrap}>
              <div className={styles.groupSelectedPreviewTop}>
                <div className={styles.selectedUsersAvatarRow}>
                  {uniqueSelectedUsers.slice(0, 10).map((u) => (
                    <div key={u.id} className={styles.selectedAvatarCircle}>
                      {initials(u.full_name || `${t("log_user")} ${u.ui_id}`)}
                    </div>
                  ))}

                  {uniqueSelectedUsers.length > 10 && (
                    <div className={styles.selectedAvatarMore}>
                      +{uniqueSelectedUsers.length - 10}
                    </div>
                  )}
                </div>

                <div className={styles.selectedCountLine}>{selectedCount} {t("chat_selected")}</div>
              </div>

              <input
                className={styles.newChatInput}
                value={groupTitle}
                onChange={(e) => setGroupTitle(e.target.value)}
                placeholder={t("chat_group_title")}
                autoFocus
              />
            </div>

            <div className={styles.pickerFooterInline}>
              <button
                type="button"
                className={styles.primaryBtn}
                disabled={!groupTitle.trim() || !selectedUsers.length}
                onClick={createGroupByUiIds}
              >
                {selectedCount ? `${t("chat_create_group")} (${selectedCount})` : t("chat_create_group")}
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.pickerScreen}>
            {pickerMode === "group" && !!uniqueSelectedUsers.length && (
              <div className={styles.groupComposerCompact}>
                <div className={styles.selectedUsersAvatarRow}>
                  {uniqueSelectedUsers.slice(0, 8).map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      className={styles.selectedAvatarBtn}
                      onClick={() => toggleSelectUser(u)}
                      title={`${t("chat_remove")}: ${u.full_name || `${t("log_user")} ${u.ui_id}`}`}
                    >
                      <div className={styles.selectedAvatarCircle}>
                        {initials(u.full_name || `${t("log_user")} ${u.ui_id}`)}
                      </div>
                    </button>
                  ))}

                  {uniqueSelectedUsers.length > 8 && (
                    <div className={styles.selectedAvatarMore}>
                      +{uniqueSelectedUsers.length - 8}
                    </div>
                  )}
                </div>

                <div className={styles.selectedCountLine}>{selectedCount} {t("chat_selected")}</div>
              </div>
            )}

            <div className={styles.searchBox}>
              <input
                className={styles.searchInput}
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder={t("chat_search_users")}
                autoFocus
              />
            </div>

            <div className={styles.userPickerList}>
              {usersLoading ? <div className={styles.hint}>{t("loading")}</div> : null}

              {shouldShowRecentUsers && (
                <>
                  <div className={styles.sectionLabel}>{t("chat_recent")}</div>
                  {filteredRecentUsers.map((u) => {
                    const checked = selectedUsers.some((x) => Number(x.id) === Number(u.id));

                    return (
                      <button
                        key={`recent_${u.id}`}
                        type="button"
                        className={`${styles.userRowCard} ${checked ? styles.userRowCardSelected : ""}`}
                        onClick={() => {
                          if (pickerMode === "direct") {
                            createDirectByUiId(u.ui_id);
                            return;
                          }
                          toggleSelectUser(u);
                        }}
                        title={renderSearchLabel(u)}
                      >
                        <div className={styles.userRowAvatar}>{initials(u.full_name)}</div>

                        <div className={styles.userRowBody}>
                          <div className={styles.userRowName}>{u.full_name || `${t("log_user")} ${u.ui_id}`}</div>
                          <div className={styles.userRowMeta}>
                            UI ID: {u.ui_id ?? t("n_a")}
                            {u.email ? ` · ${u.email}` : ""}
                          </div>
                        </div>

                        {pickerMode === "group" && (
                          <div className={`${styles.userPickerCheck} ${checked ? styles.userPickerCheckOn : ""}`}>
                            {checked ? "✓" : ""}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </>
              )}

              {shouldShowUserResults && (
                <>
                  <div className={styles.sectionLabel}>{t("chat_search_results")}</div>
                  {filteredSearchResults.map((u) => {
                    const checked = selectedUsers.some((x) => Number(x.id) === Number(u.id));

                    return (
                      <button
                        key={`search_${u.id}`}
                        type="button"
                        className={`${styles.userRowCard} ${checked ? styles.userRowCardSelected : ""}`}
                        onClick={() => {
                          if (pickerMode === "direct") {
                            createDirectByUiId(u.ui_id);
                            return;
                          }
                          toggleSelectUser(u);
                        }}
                        title={renderSearchLabel(u)}
                      >
                        <div className={styles.userRowAvatar}>{initials(u.full_name)}</div>

                        <div className={styles.userRowBody}>
                          <div className={styles.userRowName}>{u.full_name || `${t("log_user")} ${u.ui_id}`}</div>
                          <div className={styles.userRowMeta}>
                            UI ID: {u.ui_id ?? t("n_a")}
                            {u.email ? ` · ${u.email}` : ""}
                          </div>
                        </div>

                        {pickerMode === "group" && (
                          <div className={`${styles.userPickerCheck} ${checked ? styles.userPickerCheckOn : ""}`}>
                            {checked ? "✓" : ""}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            {pickerMode === "group" && (
              <div className={styles.pickerFooterInline}>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  disabled={!selectedUsers.length}
                  onClick={() => setGroupStep(2)}
                >
                  {selectedCount ? `${t("next")} (${selectedCount})` : t("next")}
                </button>
              </div>
            )}
          </div>
        )}
      </aside>

      <main className={styles.main}>
        {!activeThread ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>{t("chat_select_a_chat")}</div>
            <div className={styles.emptySub}>{t("chat_choose_thread_left")}</div>
          </div>
        ) : (
          <>
            <div className={styles.mainHeader}>
              <div className={styles.mainHeaderLeft}>
                <div className={styles.mainTitle}>{getThreadTitle(activeThread)}</div>
                <div className={styles.mainSub}>
                  {typingList.length ? (
                    <div className={styles.typingIndicator}>
                      <span>{typingList.join(", ")}</span>
                      <div className={styles.typingDots}>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  ) : (
                    getThreadSubtitle(activeThread)
                  )}
                </div>
              </div>

              <div className={styles.mainHeaderRight}>
                {nextCursor && (
                  <button
                    className={styles.ghostBtn}
                    onClick={loadOlder}
                    disabled={messagesLoading}
                    type="button"
                  >
                    {t("chat_load_older")}
                  </button>
                )}
              </div>
            </div>

            {messagesError && <div className={styles.alertError}>{messagesError}</div>}

            <div className={styles.messages} ref={listRef}>
              {messagesLoading && messages.length === 0 && (
                <div className={styles.hint}>{t("chat_loading_messages")}</div>
              )}

              {messages.map((m) => {
                const mine = me.id && Number(m.sender_id) === Number(me.id);
                const senderName = m.sender?.full_name || (mine ? t("chat_me") : t("chat_unknown"));

                const threadId = Number(activeThreadId);
                const participantsArr = Array.isArray(activeThread?.participants)
                  ? activeThread.participants
                  : [];
                const others = participantsArr
                  .map((p) => Number(p.id))
                  .filter((id) => id && id !== Number(me.id));

                const seen =
                  mine &&
                  others.some((uid) => {
                    const lastRead = Number(readMap?.[threadId]?.[uid] || 0);
                    return lastRead >= Number(m.id);
                  });

                return (
                  <div
                    key={m.id}
                    className={`${styles.msgRow} ${mine ? styles.msgRowMine : styles.msgRowTheirs}`}
                  >
                    <div className={styles.msgBubble}>
                      <div className={styles.msgMeta}>
                        {mine && (
                          <span
                            className={`${styles.tick} ${seen ? styles.tickSeen : ""}`}
                            title={seen ? t("chat_seen") : t("chat_sent")}
                          >
                            {seen ? "✓✓" : "✓"}
                          </span>
                        )}
                        <span className={styles.msgSender}>{senderName}</span>
                        <span className={styles.msgTime}>{formatTime(m.created_at)}</span>
                      </div>
                      <div className={styles.msgBody}>{m.is_deleted ? t("chat_message_deleted") : m.body}</div>
                    </div>
                  </div>
                );
              })}

              <div ref={bottomRef} />
            </div>

            <div className={styles.composer}>
              <div className={styles.textareaWrap}>
                <textarea
                  className={styles.textarea}
                  value={text}
                  onChange={(e) => {
                    const v = e.target.value;
                    setText(v);

                    const now = Date.now();
                    if (now - lastTypingSentRef.current > TYPING_THROTTLE_MS) {
                      lastTypingSentRef.current = now;
                      wsSend({ type: "typing", is_typing: true });
                    }

                    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
                    typingTimerRef.current = setTimeout(() => {
                      wsSend({ type: "typing", is_typing: false });
                    }, TYPING_STOP_MS);
                  }}
                  onKeyDown={onEnterSend}
                  placeholder={t("chat_type_something")}
                  disabled={sending}
                />

                <button
                  ref={emojiButtonRef}
                  type="button"
                  className={styles.emojiInlineBtn}
                  onClick={() => setShowEmojiPicker((prev) => !prev)}
                  title={t("chat_emoji")}
                >
                  <span className={styles.emojiInlineIcon}>😊</span>
                </button>

                {showEmojiPicker && (
                  <div ref={emojiPickerRef} className={styles.emojiPickerPopover}>
                    <EmojiPicker
                      onEmojiClick={onEmojiClick}
                      theme="dark"
                      lazyLoadEmojis={true}
                      searchDisabled={false}
                      skinTonesDisabled={false}
                      previewConfig={{ showPreview: false }}
                      width={320}
                      height={380}
                    />
                  </div>
                )}
              </div>

              <button
                className={styles.sendBtn}
                onClick={sendMessage}
                disabled={sending || !text.trim()}
                type="button"
              >
                {sending ? t("sending") : t("chat_send")}
              </button>
            </div>
          </>
        )}
      </main>

      <aside className={styles.rightPanel}>
        {!activeThread ? (
          <div className={styles.rightEmpty}>
            <div className={styles.hint}>{t("chat_select_chat_for_details")}</div>
          </div>
        ) : (
          <>
            <div className={styles.rightHeader}>
              <div className={styles.rightAvatar}>{initials(getThreadTitle(activeThread))}</div>
              <div className={styles.rightHeadText}>
                <div className={styles.rightTitle}>{getThreadTitle(activeThread)}</div>
                <div className={styles.rightMuted}>
                  {activeThread.type === "group"
                    ? `${participants.length} ${t("chat_members")}`
                    : directPartner
                    ? `UI ID: ${directPartner.ui_id ?? t("n_a")}`
                    : t("chat_direct_message")}
                </div>
              </div>
            </div>

            <div className={styles.rightSection}>
              <div className={styles.rightSectionTitle}>
                {activeThread.type === "group" ? t("chat_members") : t("chat_contact")}
              </div>

              <div className={styles.memberList}>
                {participants.length ? (
                  (activeThread.type === "direct" && directPartner ? [directPartner] : participants).map((p) => {
                    const uid = Number(p.id);
                    const isMe = me.id && uid === Number(me.id);
                    const status = globalPresence[uid] || presence[uid] || "offline";

                    return (
                      <div key={p.id} className={styles.memberItem}>
                        <div className={styles.memberAvatarWrap}>
                          <div className={styles.memberAvatar}>{initials(p.full_name)}</div>

                          <span
                            className={`${styles.statusDotOnAvatar} ${
                              status === "online" ? styles.statusOnline : styles.statusOffline
                            }`}
                            title={status === "online" ? t("chat_online") : t("chat_offline")}
                          />
                        </div>

                        <div className={styles.memberText}>
                          <div className={styles.memberName}>
                            {p.full_name || `${t("log_user")} ${p.id}`}
                            {isMe && <span className={styles.meTag}>{t("chat_me_tag")}</span>}
                          </div>
                          <div className={styles.memberEmail}>{p.email || t("n_a")}</div>
                          <div className={styles.memberUiId}>UI ID: {p.ui_id ?? t("n_a")}</div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.hint}>{t("chat_no_participant_data")}</div>
                )}
              </div>
            </div>

            <div className={styles.rightSection}>
              <div className={styles.rightSectionTitle}>{t("actions")}</div>
              <div className={styles.actionGrid}>
                <button
                  className={styles.ghostBtn}
                  type="button"
                  onClick={() => {
                    setShowCreateMenu(false);
                    setPickerMode("direct");
                    setGroupStep(1);
                  }}
                >
                  {t("chat_create_new_chat")}
                </button>
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}